import React, { useState, useEffect } from 'react';
import { FiDownload, FiFilter, FiBarChart2, FiTrendingUp, FiAlertTriangle, FiShoppingCart } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { reportsAPI, categoriesAPI } from '../../services/api';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

const Reports = () => {
  const [activeTab, setActiveTab] = useState('stock-summary');
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);

  // Filters
  const [filters, setFilters] = useState({
    date_from: '',
    date_to: '',
    category_id: '',
    product_id: '',
    location: '',
    invoice_type: '',
    payment_status: '',
    customer_name: '',
    supplier_name: ''
  });

  // Report Data
  const [reportData, setReportData] = useState(null);

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await categoriesAPI.getActive();
      setCategories(response.data.data || []);
      console.log(response.data)
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const fetchReport = async () => {
    try {
      setLoading(true);
      let response;

      switch (activeTab) {
        case 'stock-summary':
          response = await reportsAPI.getStockSummary({
            category_id: filters.category_id
          });
          break;
        case 'product-movement':
          if (!filters.date_from || !filters.date_to) {
            toast.error('Please select date range');
            return;
          }
          response = await reportsAPI.getProductMovement({
            date_from: filters.date_from,
            date_to: filters.date_to,
            product_id: filters.product_id,
            location: filters.location
          });
          break;
        case 'invoice-history':
          response = await reportsAPI.getInvoiceHistory({
            date_from: filters.date_from,
            date_to: filters.date_to,
            invoice_type: filters.invoice_type,
            payment_status: filters.payment_status,
            customer_name: filters.customer_name
          });
          break;
        case 'low-stock':
          response = await reportsAPI.getLowStock({
            category_id: filters.category_id
          });
          break;
        case 'sales-summary':
          if (!filters.date_from || !filters.date_to) {
            toast.error('Please select date range');
            return;
          }
          response = await reportsAPI.getSalesSummary({
            date_from: filters.date_from,
            date_to: filters.date_to
          });
          break;
        case 'location-stock':
          response = await reportsAPI.getLocationStock({
            category_id: filters.category_id
          });
          break;
        case 'party-ledger':
          response = await reportsAPI.getPartyLedger({
            customer_name: filters.customer_name,
            date_from: filters.date_from,
            date_to: filters.date_to
          });
          break;
        case 'purchase-bills':
          if (!filters.date_from || !filters.date_to) {
            toast.error('Please select date range');
            return;
          }
          response = await reportsAPI.getPurchaseBills({
            date_from: filters.date_from,
            date_to: filters.date_to,
            payment_status: filters.payment_status,
            supplier_name: filters.supplier_name
          });
          break;
        default:
          break;
      }

      setReportData(response.data.data);
    } catch (error) {
      toast.error('Failed to fetch report');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const exportToExcel = () => {
    if (!reportData) {
      toast.error('No data to export');
      return;
    }

    try {
      let sheetData = [];
      let fileName = 'Report';

      switch (activeTab) {
        case 'stock-summary':
          fileName = 'Stock_Summary_Report';
          sheetData = (reportData.products || []).map((p, i) => ({
            'S.No': i + 1,
            'Item Code': p.item_code,
            'Product Name': p.name,
            'Category': p.category_name || '',
            'Sub Category': p.sub_category_name || '',
            'Unit': p.unit,
            'Price': parseFloat(p.price || 0),
            'Office Stock': parseInt(p.stock_office || 0),
            'Godown Stock': parseInt(p.stock_godown || 0),
            'Factory Stock': parseInt(p.stock_factory || 0),
            'Total Stock': parseInt(p.total_stock || 0),
            'Stock Value': parseFloat(p.stock_value || 0),
            'Status': p.stock_status || ''
          }));
          break;

        case 'product-movement':
          fileName = 'Product_Movement_Report';
          sheetData = (reportData.movements || []).map((m, i) => ({
            'S.No': i + 1,
            'Date': m.date ? new Date(m.date).toLocaleDateString() : '',
            'Type': m.transaction_type,
            'Product': m.product_name || '',
            'Item Code': m.item_code || '',
            'Location': m.location || '',
            'Qty In': parseInt(m.quantity || 0),
            'Qty Out': parseInt(m.quantity_out || 0),
            'Unit Price': parseFloat(m.unit_price || 0),
            'Amount': parseFloat(m.amount || 0),
            'Balance': parseInt(m.closing_stock || 0),
            'Party Name': m.party_name || '',
            'Remarks': m.remarks || ''
          }));
          break;

        case 'invoice-history':
          fileName = 'Invoice_History_Report';
          sheetData = (reportData.invoices || []).map((inv, i) => ({
            'S.No': i + 1,
            'Invoice #': inv.invoice_number || '',
            // 'Source': inv.source === 'stock_out' ? 'Stock Out' : 'Invoice',
            'Date': inv.date ? new Date(inv.date).toLocaleDateString() : '',
            'Customer Name': inv.customer_name || '',
            'Customer Phone': inv.customer_phone || '',
            'Company': inv.company_name || '',
            'Invoice Type': (inv.invoice_type || '').replace(/_/g, ' '),
            'Total Amount': parseFloat(inv.total_amount || 0),
            'Paid Amount': parseFloat(inv.paid_amount || 0),
            'Due Amount': parseFloat(inv.due_amount || 0),
            'Tax Amount': parseFloat(inv.tax_amount || 0),
            'Payment Status': inv.payment_status || 'unpaid',
            'Payment Mode': inv.payment_mode || '',
            'Delivery Status': inv.delivery_status || 'pending',
            'Created By': inv.created_by_name || ''
          }));
          break;

        case 'low-stock':
          fileName = 'Low_Stock_Alert_Report';
          sheetData = (reportData.products || []).map((p, i) => ({
            'S.No': i + 1,
            'Item Code': p.item_code,
            'Product Name': p.name,
            'Category': p.category_name || '',
            'Unit': p.unit,
            'Office Stock': parseInt(p.stock_office || 0),
            'Godown Stock': parseInt(p.stock_godown || 0),
            'Factory Stock': parseInt(p.stock_factory || 0),
            'Total Stock': parseInt(p.total_stock || 0),
            'Threshold': parseInt(p.low_stock_threshold || 0),
            'Shortage': parseInt(p.shortage || 0),
            'Alert Level': (p.alert_level || '').toUpperCase(),
            'Price': parseFloat(p.price || 0)
          }));
          break;

        case 'sales-summary': {
          fileName = 'Sales_Summary_Report';
          const wb = XLSX.utils.book_new();

          // Daily Summary sheet
          if (reportData.daily_summary && reportData.daily_summary.length > 0) {
            const dailyData = reportData.daily_summary.map((d, i) => ({
              'S.No': i + 1,
              'Date': d.date ? new Date(d.date).toLocaleDateString() : '',
              'Invoice Count': parseInt(d.invoice_count || 0),
              'Total Amount': parseFloat(d.total_amount || 0),
              'Total Tax': parseFloat(d.total_tax || 0)
            }));
            const ws1 = XLSX.utils.json_to_sheet(dailyData);
            autoFitColumns(ws1, dailyData);
            XLSX.utils.book_append_sheet(wb, ws1, 'Daily Summary');
          }

          // Payment Status sheet
          if (reportData.payment_status_summary && reportData.payment_status_summary.length > 0) {
            const statusData = reportData.payment_status_summary.map((s, i) => ({
              'S.No': i + 1,
              'Payment Status': (s.payment_status || '').toUpperCase(),
              'Count': parseInt(s.count || 0),
              'Total Amount': parseFloat(s.total_amount || 0)
            }));
            const ws2 = XLSX.utils.json_to_sheet(statusData);
            autoFitColumns(ws2, statusData);
            XLSX.utils.book_append_sheet(wb, ws2, 'Payment Status');
          }

          // Overall Summary sheet
          if (reportData.overall_summary) {
            const os = reportData.overall_summary;
            const overallData = [
              { 'Metric': 'Total Records', 'Value': os.total_invoices || 0 },
              { 'Metric': 'Invoices', 'Value': os.invoice_count || 0 },
              { 'Metric': 'Stock Out', 'Value': os.stock_out_count || 0 },
              { 'Metric': 'Total Sales', 'Value': parseFloat(os.total_sales || 0) },
              { 'Metric': 'Total Paid', 'Value': parseFloat(os.total_paid || 0) },
              { 'Metric': 'Total Due', 'Value': parseFloat(os.total_due || 0) },
              { 'Metric': 'Tax Collected', 'Value': parseFloat(os.total_tax_collected || 0) },
              { 'Metric': 'Avg Invoice Value', 'Value': parseFloat(os.average_invoice_value || 0) }
            ];
            const ws3 = XLSX.utils.json_to_sheet(overallData);
            autoFitColumns(ws3, overallData);
            XLSX.utils.book_append_sheet(wb, ws3, 'Overall Summary');
          }

          const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
          const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
          saveAs(blob, `${fileName}_${new Date().toISOString().split('T')[0]}.xlsx`);
          toast.success('Excel file downloaded successfully!');
          return;
        }

        case 'location-stock':
          fileName = 'Location_Stock_Report';
          sheetData = (reportData.products || []).map((p, i) => ({
            'S.No': i + 1,
            'Item Code': p.item_code,
            'Product Name': p.name,
            'Category': p.category_name || '',
            'Unit': p.unit,
            'Price': parseFloat(p.price || 0),
            'Office Stock': parseInt(p.stock_office || 0),
            'Office Value': parseFloat(p.office_value || 0),
            'Godown Stock': parseInt(p.stock_godown || 0),
            'Godown Value': parseFloat(p.godown_value || 0),
            'Factory Stock': parseInt(p.stock_factory || 0),
            'Factory Value': parseFloat(p.factory_value || 0),
            'Total Stock': parseInt(p.total_stock || 0),
            'Total Value': parseFloat(p.total_value || 0)
          }));
          break;

        case 'party-ledger':
          fileName = 'Party_Ledger_Report';
          sheetData = (reportData.ledger || []).map((c, i) => ({
            'S.No': i + 1,
            'Customer Name': c.customer_name || '',
            'Source': c.source === 'stock_out' ? 'Stock Out' : 'Invoice',
            'Company': c.company_name || '',
            'Phone': c.customer_phone || '',
            'Email': c.customer_email || '',
            'Total Transactions': parseInt(c.total_invoices || 0),
            'Total Amount': parseFloat(c.total_amount || 0),
            'Total Paid': parseFloat(c.total_paid || 0),
            'Total Unpaid': parseFloat(c.total_unpaid || 0),
            'Last Date': c.last_invoice_date ? new Date(c.last_invoice_date).toLocaleDateString() : ''
          }));
          break;

        case 'purchase-bills': {
          fileName = 'Purchase_Bills_Report';
          const wb2 = XLSX.utils.book_new();

          // Bill Details sheet
          if (reportData.bills && reportData.bills.length > 0) {
            const billData = reportData.bills.map((b, i) => ({
              'S.No': i + 1,
              'Bill #': b.bill_number || '',
              'Date': b.bill_date ? new Date(b.bill_date).toLocaleDateString() : '',
              'Supplier': b.supplier_name || '',
              'Contact': b.supplier_contact || '',
              'GSTIN': b.supplier_gstin || '',
              'Subtotal': parseFloat(b.subtotal || 0),
              'Tax Amount': parseFloat(b.tax_amount || 0),
              'Discount': parseFloat(b.discount_amount || 0),
              'Total Amount': parseFloat(b.total_amount || 0),
              'Paid Amount': parseFloat(b.paid_amount || 0),
              'Due Amount': parseFloat(b.due_amount || 0),
              'Payment Status': b.payment_status || '',
              'Payment Mode': b.payment_mode || '',
              'Created By': b.created_by_name || ''
            }));
            const ws1 = XLSX.utils.json_to_sheet(billData);
            autoFitColumns(ws1, billData);
            XLSX.utils.book_append_sheet(wb2, ws1, 'Bill Details');
          }

          // Monthly Summary sheet
          if (reportData.monthly_summary && reportData.monthly_summary.length > 0) {
            const monthlyData = reportData.monthly_summary.map((m, i) => ({
              'S.No': i + 1,
              'Month': m.month_name || '',
              'Bill Count': parseInt(m.bill_count || 0),
              'Total Amount': parseFloat(m.total_amount || 0),
              'Total Tax': parseFloat(m.total_tax || 0),
              'Total Paid': parseFloat(m.total_paid || 0),
              'Total Due': parseFloat(m.total_due || 0)
            }));
            const ws2 = XLSX.utils.json_to_sheet(monthlyData);
            autoFitColumns(ws2, monthlyData);
            XLSX.utils.book_append_sheet(wb2, ws2, 'Monthly Summary');
          }

          // Top Suppliers sheet
          if (reportData.top_suppliers && reportData.top_suppliers.length > 0) {
            const supplierData = reportData.top_suppliers.map((s, i) => ({
              'S.No': i + 1,
              'Supplier': s.supplier_name || '',
              'Bill Count': parseInt(s.bill_count || 0),
              'Total Amount': parseFloat(s.total_amount || 0)
            }));
            const ws3 = XLSX.utils.json_to_sheet(supplierData);
            autoFitColumns(ws3, supplierData);
            XLSX.utils.book_append_sheet(wb2, ws3, 'Top Suppliers');
          }

          const excelBuffer2 = XLSX.write(wb2, { bookType: 'xlsx', type: 'array' });
          const blob2 = new Blob([excelBuffer2], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
          saveAs(blob2, `${fileName}_${new Date().toISOString().split('T')[0]}.xlsx`);
          toast.success('Excel file downloaded successfully!');
          return;
        }

        default:
          toast.error('Export not available for this report');
          return;
      }

      if (sheetData.length === 0) {
        toast.error('No data to export');
        return;
      }

      // Create workbook with single sheet
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(sheetData);
      autoFitColumns(ws, sheetData);
      XLSX.utils.book_append_sheet(wb, ws, 'Report');

      const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      saveAs(blob, `${fileName}_${new Date().toISOString().split('T')[0]}.xlsx`);
      toast.success('Excel file downloaded successfully!');
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export report');
    }
  };

  // Auto-fit column widths
  const autoFitColumns = (ws, data) => {
    if (!data || data.length === 0) return;
    const colWidths = Object.keys(data[0]).map(key => {
      const maxLen = Math.max(
        key.length,
        ...data.map(row => String(row[key] ?? '').length)
      );
      return { wch: Math.min(maxLen + 2, 40) };
    });
    ws['!cols'] = colWidths;
  };

  const tabs = [
    { id: 'stock-summary', label: 'Stock Summary', icon: FiBarChart2 },
    { id: 'product-movement', label: 'Product Movement', icon: FiTrendingUp },
    { id: 'invoice-history', label: 'Invoice History', icon: FiBarChart2 },
    { id: 'low-stock', label: 'Low Stock Alerts', icon: FiAlertTriangle },
    { id: 'sales-summary', label: 'Sales Summary', icon: FiTrendingUp },
    { id: 'location-stock', label: 'Location-wise Stock', icon: FiBarChart2 },
    // { id: 'party-ledger', label: 'Party Ledger', icon: FiBarChart2 },
    { id: 'purchase-bills', label: 'Purchase Bills', icon: FiShoppingCart }
  ];

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Reports & Analytics</h1>
          <p className="text-gray-600 text-sm mt-1">Generate comprehensive business reports</p>
        </div>
        {reportData && (
          <button
            onClick={exportToExcel}
            className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
          >
            <FiDownload size={18} />
            Export Excel
          </button>
        )}
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-lg shadow-sm mb-6">
        <div className="border-b border-gray-200">
          <div className="flex overflow-x-auto">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => {
                    setActiveTab(tab.id);
                    setReportData(null);
                  }}
                  className={`flex items-center gap-2 px-6 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                    activeTab === tab.id
                      ? 'border-blue-600 text-blue-600'
                      : 'border-transparent text-gray-600 hover:text-gray-800'
                  }`}
                >
                  <Icon size={16} />
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
        <div className="flex items-center gap-2 mb-4">
          <FiFilter className="text-gray-500" />
          <span className="font-medium text-gray-700">Filters</span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Date Range Filters */}
          {['product-movement', 'invoice-history', 'sales-summary', 'party-ledger', 'purchase-bills'].includes(activeTab) && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">From Date</label>
                <input
                  type="date"
                  name="date_from"
                  value={filters.date_from}
                  onChange={handleFilterChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">To Date</label>
                <input
                  type="date"
                  name="date_to"
                  value={filters.date_to}
                  onChange={handleFilterChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </>
          )}

          {/* Category Filter */}
          {['stock-summary', 'low-stock', 'location-stock'].includes(activeTab) && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
              <select
                name="category_id"
                value={filters.category_id}
                onChange={handleFilterChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Categories</option>
                {categories.map(cat => (
                  <option key={cat.id} value={cat.id}>{cat.name}</option>
                ))}
              </select>
            </div>
          )}

          {/* Location Filter */}
          {activeTab === 'product-movement' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
              <select
                name="location"
                value={filters.location}
                onChange={handleFilterChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Locations</option>
                <option value="office">Office</option>
                <option value="godown">Godown</option>
                <option value="factory">Factory</option>
              </select>
            </div>
          )}

          {/* Invoice Type Filter */}
          {activeTab === 'invoice-history' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Invoice Type</label>
              <select
                name="invoice_type"
                value={filters.invoice_type}
                onChange={handleFilterChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Types</option>
                <option value="tax_invoice">Tax Invoice</option>
                <option value="proforma">Proforma</option>
                <option value="delivery_challan">Delivery Challan</option>
                <option value="bill_book">Bill Book</option>
              </select>
            </div>
          )}

          {/* Payment Status Filter */}
          {['invoice-history', 'purchase-bills'].includes(activeTab) && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Payment Status</label>
              <select
                name="payment_status"
                value={filters.payment_status}
                onChange={handleFilterChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Status</option>
                <option value="unpaid">Unpaid</option>
                <option value="partial">Partial</option>
                <option value="paid">Paid</option>
              </select>
            </div>
          )}

          {/* Supplier Name Filter */}
          {activeTab === 'purchase-bills' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Supplier Name</label>
              <input
                type="text"
                name="supplier_name"
                value={filters.supplier_name}
                onChange={handleFilterChange}
                placeholder="Search supplier..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
          )}

          {/* Customer Name Filter */}
          {['invoice-history', 'party-ledger'].includes(activeTab) && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Customer Name</label>
              <input
                type="text"
                name="customer_name"
                value={filters.customer_name}
                onChange={handleFilterChange}
                placeholder="Search customer..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
          )}
        </div>

        <div className="mt-4">
          <button
            onClick={fetchReport}
            disabled={loading}
            className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg flex items-center gap-2 transition-colors disabled:opacity-50"
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                Generating...
              </>
            ) : (
              <>
                <FiBarChart2 size={16} />
                Generate Report
              </>
            )}
          </button>
        </div>
      </div>

      {/* Report Display */}
      {reportData && (
        <div className="bg-white rounded-lg shadow-sm p-6">
          {activeTab === 'stock-summary' && <StockSummaryReport data={reportData} />}
          {activeTab === 'product-movement' && <ProductMovementReport data={reportData} />}
          {activeTab === 'invoice-history' && <InvoiceHistoryReport data={reportData} />}
          {activeTab === 'low-stock' && <LowStockReport data={reportData} />}
          {activeTab === 'sales-summary' && <SalesSummaryReport data={reportData} />}
          {activeTab === 'location-stock' && <LocationStockReport data={reportData} />}
          {activeTab === 'party-ledger' && <PartyLedgerReport data={reportData} />}
          {activeTab === 'purchase-bills' && <PurchaseBillsReport data={reportData} />}

          {/* Export Button at bottom */}
          <div className="mt-6 pt-4 border-t border-gray-200 flex justify-end">
            <button
              onClick={exportToExcel}
              className="bg-green-600 hover:bg-green-700 text-white px-6 py-2.5 rounded-lg flex items-center gap-2 transition-colors shadow-sm"
            >
              <FiDownload size={18} />
              Download Excel Report
            </button>
          </div>
        </div>
      )}

      {!reportData && !loading && (
        <div className="bg-white rounded-lg shadow-sm p-12 text-center">
          <FiBarChart2 className="mx-auto text-gray-400 mb-4" size={64} />
          <h3 className="text-xl font-semibold text-gray-800 mb-2">No Report Generated</h3>
          <p className="text-gray-600">Select filters and click "Generate Report" to view data</p>
        </div>
      )}
    </div>
  );
};

// Report Components
const StockSummaryReport = ({ data }) => (
  <div>
    <h3 className="text-lg font-semibold mb-4">Stock Summary Report</h3>

    {/* Summary Cards */}
    <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
      <div className="bg-blue-50 p-4 rounded-lg">
        <div className="text-sm text-blue-600 mb-1">Total Products</div>
        <div className="text-2xl font-bold text-blue-900">{data.totals.total_products}</div>
      </div>
      <div className="bg-green-50 p-4 rounded-lg">
        <div className="text-sm text-green-600 mb-1">Stock Value</div>
        <div className="text-2xl font-bold text-green-900">₹{data.totals.total_stock_value.toFixed(2)}</div>
      </div>
      <div className="bg-yellow-50 p-4 rounded-lg">
        <div className="text-sm text-yellow-600 mb-1">Low Stock</div>
        <div className="text-2xl font-bold text-yellow-900">{data.totals.low_stock_count}</div>
      </div>
      <div className="bg-red-50 p-4 rounded-lg">
        <div className="text-sm text-red-600 mb-1">Out of Stock</div>
        <div className="text-2xl font-bold text-red-900">{data.totals.out_of_stock_count}</div>
      </div>
    </div>

    {/* Products Table */}
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Item Code</th>
            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Product</th>
            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
            <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Office</th>
            <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Godown</th>
            <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Factory</th>
            <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Total</th>
            <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Value</th>
            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200">
          {data.products.map((product) => (
            <tr key={product.id} className="hover:bg-gray-50">
              <td className="px-4 py-2 text-sm">{product.item_code}</td>
              <td className="px-4 py-2 text-sm font-medium">{product.name}</td>
              <td className="px-4 py-2 text-sm">{product.category_name}</td>
              <td className="px-4 py-2 text-sm text-right">{product.stock_office}</td>
              <td className="px-4 py-2 text-sm text-right">{product.stock_godown}</td>
              <td className="px-4 py-2 text-sm text-right">{product.stock_factory}</td>
              <td className="px-4 py-2 text-sm text-right font-medium">{product.total_stock}</td>
              <td className="px-4 py-2 text-sm text-right">₹{parseFloat(product.stock_value).toFixed(2)}</td>
              <td className="px-4 py-2 text-sm">
                <span className={`px-2 py-1 rounded-full text-xs ${
                  product.stock_status === 'Out of Stock' ? 'bg-red-100 text-red-800' :
                  product.stock_status === 'Low Stock' ? 'bg-yellow-100 text-yellow-800' :
                  'bg-green-100 text-green-800'
                }`}>
                  {product.stock_status}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

const ProductMovementReport = ({ data }) => (
  <div>
    <h3 className="text-lg font-semibold mb-4">Product Movement Report</h3>

    {/* Summary */}
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
      <div className="bg-green-50 p-4 rounded-lg">
        <div className="text-sm text-green-600 mb-1">Total Stock In</div>
        <div className="text-2xl font-bold text-green-900">{data.summary.total_stock_in}</div>
        <div className="text-xs text-green-600 mt-1">₹{data.summary.total_in_value.toFixed(2)}</div>
      </div>
      <div className="bg-red-50 p-4 rounded-lg">
        <div className="text-sm text-red-600 mb-1">Total Stock Out</div>
        <div className="text-2xl font-bold text-red-900">{data.summary.total_stock_out}</div>
        <div className="text-xs text-red-600 mt-1">₹{data.summary.total_out_value.toFixed(2)}</div>
      </div>
      <div className="bg-blue-50 p-4 rounded-lg">
        <div className="text-sm text-blue-600 mb-1">Net Movement</div>
        <div className="text-2xl font-bold text-blue-900">{data.summary.net_movement}</div>
      </div>
    </div>

    {/* Movements Table */}
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Product</th>
            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Location</th>
            <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">In</th>
            <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Out</th>
            <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Balance</th>
            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Party</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200">
          {data.movements.map((movement, index) => (
            <tr key={index} className="hover:bg-gray-50">
              <td className="px-4 py-2 text-sm">{new Date(movement.date).toLocaleDateString()}</td>
              <td className="px-4 py-2 text-sm">
                <span className={`px-2 py-1 rounded-full text-xs ${
                  movement.transaction_type === 'Stock In' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                }`}>
                  {movement.transaction_type}
                </span>
              </td>
              <td className="px-4 py-2 text-sm">{movement.product_name}</td>
              <td className="px-4 py-2 text-sm">{movement.location}</td>
              <td className="px-4 py-2 text-sm text-right text-green-600">{movement.quantity || '-'}</td>
              <td className="px-4 py-2 text-sm text-right text-red-600">{movement.quantity_out || '-'}</td>
              <td className="px-4 py-2 text-sm text-right font-medium">{movement.closing_stock}</td>
              <td className="px-4 py-2 text-sm">{movement.party_name || '-'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

// Other report components...
const InvoiceHistoryReport = ({ data }) => (
  <div>
    <h3 className="text-lg font-semibold mb-4">Invoice History Report</h3>
    <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
      <div className="bg-blue-50 p-4 rounded-lg">
        <div className="text-sm text-blue-600 mb-1">Total Records</div>
        <div className="text-2xl font-bold text-blue-900">{data.summary.total_invoices}</div>
      </div>
      <div className="bg-indigo-50 p-4 rounded-lg">
        <div className="text-sm text-indigo-600 mb-1">Total Amount</div>
        <div className="text-2xl font-bold text-indigo-900">₹{parseFloat(data.summary.total_amount || 0).toFixed(2)}</div>
      </div>
      <div className="bg-green-50 p-4 rounded-lg">
        <div className="text-sm text-green-600 mb-1">Total Paid</div>
        <div className="text-2xl font-bold text-green-900">₹{parseFloat(data.summary.total_paid || 0).toFixed(2)}</div>
      </div>
      <div className="bg-red-50 p-4 rounded-lg">
        <div className="text-sm text-red-600 mb-1">Total Due</div>
        <div className="text-2xl font-bold text-red-900">₹{parseFloat(data.summary.total_due || 0).toFixed(2)}</div>
      </div>
      <div className="bg-yellow-50 p-4 rounded-lg">
        <div className="text-sm text-yellow-600 mb-1">Total Tax</div>
        <div className="text-2xl font-bold text-yellow-900">₹{parseFloat(data.summary.total_tax || 0).toFixed(2)}</div>
      </div>
      <div className="bg-purple-50 p-4 rounded-lg">
        <div className="text-sm text-purple-600 mb-1">Paid / Unpaid</div>
        <div className="text-lg font-bold text-purple-900">{data.summary.by_status.paid} / {data.summary.by_status.unpaid}</div>
      </div>
    </div>

    {/* Type Breakdown */}
    {data.summary.by_type && (
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <div className="bg-gray-50 p-3 rounded-lg text-center">
          <div className="text-xs text-gray-500">Tax Invoice</div>
          <div className="text-lg font-bold text-gray-800">{data.summary.by_type.tax_invoice || 0}</div>
        </div>
        <div className="bg-gray-50 p-3 rounded-lg text-center">
          <div className="text-xs text-gray-500">Proforma</div>
          <div className="text-lg font-bold text-gray-800">{data.summary.by_type.proforma || 0}</div>
        </div>
        <div className="bg-gray-50 p-3 rounded-lg text-center">
          <div className="text-xs text-gray-500">Delivery Challan</div>
          <div className="text-lg font-bold text-gray-800">{data.summary.by_type.delivery_challan || 0}</div>
        </div>
        <div className="bg-gray-50 p-3 rounded-lg text-center">
          <div className="text-xs text-gray-500">Bill Book</div>
          <div className="text-lg font-bold text-gray-800">{data.summary.by_type.bill_book || 0}</div>
        </div>
      </div>
    )}
    {/* Source Breakdown */}
    {/* {data.summary.by_source && (
      <div className="grid grid-cols-2 gap-3 mb-6 max-w-md">
        <div className="bg-blue-50 p-3 rounded-lg text-center">
          <div className="text-xs text-blue-500">From Invoices</div>
          <div className="text-lg font-bold text-blue-800">{data.summary.by_source.invoice || 0}</div>
        </div>
        <div className="bg-orange-50 p-3 rounded-lg text-center">
          <div className="text-xs text-orange-500">From Stock Out</div>
          <div className="text-lg font-bold text-orange-800">{data.summary.by_source.stock_out || 0}</div>
        </div>
      </div>
    )} */}

    {/* Invoice Table */}
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Invoice #</th>
            {/* <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Source</th> */}
            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Customer</th>
            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
            <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Amount</th>
            <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Paid</th>
            <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Due</th>
            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Payment</th>
            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Delivery</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200">
          {data.invoices.map((invoice, index) => (
            <tr key={`${invoice.source}-${invoice.id}-${index}`} className="hover:bg-gray-50">
              <td className="px-3 py-2 text-sm font-medium">{invoice.invoice_number}</td>
              {/* <td className="px-3 py-2 text-sm">
                <span className={`px-2 py-1 rounded-full text-xs ${
                  invoice.source === 'stock_out' ? 'bg-orange-100 text-orange-800' : 'bg-blue-100 text-blue-800'
                }`}>
                  {invoice.source === 'stock_out' ? 'Stock Out' : 'Invoice'}
                </span>
              </td> */}
              <td className="px-3 py-2 text-sm">{new Date(invoice.date).toLocaleDateString()}</td>
              <td className="px-3 py-2 text-sm">
                <div>{invoice.customer_name || '-'}</div>
                {invoice.customer_phone && <div className="text-xs text-gray-400">{invoice.customer_phone}</div>}
              </td>
              <td className="px-3 py-2 text-sm capitalize">{(invoice.invoice_type || '').replace(/_/g, ' ')}</td>
              <td className="px-3 py-2 text-sm text-right font-medium">₹{parseFloat(invoice.total_amount || 0).toFixed(2)}</td>
              <td className="px-3 py-2 text-sm text-right text-green-600">₹{parseFloat(invoice.paid_amount || 0).toFixed(2)}</td>
              <td className="px-3 py-2 text-sm text-right text-red-600">₹{parseFloat(invoice.due_amount || 0).toFixed(2)}</td>
              <td className="px-3 py-2 text-sm">
                <span className={`px-2 py-1 rounded-full text-xs ${
                  invoice.payment_status === 'paid' ? 'bg-green-100 text-green-800' :
                  invoice.payment_status === 'partial' ? 'bg-yellow-100 text-yellow-800' :
                  'bg-red-100 text-red-800'
                }`}>
                  {invoice.payment_status || 'unpaid'}
                </span>
              </td>
              <td className="px-3 py-2 text-sm">
                <span className={`px-2 py-1 rounded-full text-xs ${
                  invoice.delivery_status === 'delivered' ? 'bg-green-100 text-green-800' :
                  invoice.delivery_status === 'returnable_basis' ? 'bg-blue-100 text-blue-800' :
                  'bg-gray-100 text-gray-800'
                }`}>
                  {invoice.delivery_status === 'returnable_basis' ? 'Returnable Basis' : (invoice.delivery_status || 'pending')}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
    <p className="text-sm text-gray-600 mt-4">Showing {data.invoices.length} records (Invoices + Stock Out)</p>
  </div>
);

const LowStockReport = ({ data }) => (
  <div>
    <h3 className="text-lg font-semibold mb-4">Low Stock Alert Report</h3>
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
      <div className="bg-red-50 p-4 rounded-lg">
        <div className="text-sm text-red-600 mb-1">Critical</div>
        <div className="text-2xl font-bold text-red-900">{data.summary.critical}</div>
      </div>
      <div className="bg-orange-50 p-4 rounded-lg">
        <div className="text-sm text-orange-600 mb-1">High</div>
        <div className="text-2xl font-bold text-orange-900">{data.summary.high}</div>
      </div>
      <div className="bg-yellow-50 p-4 rounded-lg">
        <div className="text-sm text-yellow-600 mb-1">Medium</div>
        <div className="text-2xl font-bold text-yellow-900">{data.summary.medium}</div>
      </div>
    </div>
    <p className="text-sm text-gray-600">Total {data.summary.total_low_stock} products need attention</p>
  </div>
);

const SalesSummaryReport = ({ data }) => (
  <div>
    <h3 className="text-lg font-semibold mb-4">Sales Summary Report</h3>
    <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
      <div className="bg-blue-50 p-4 rounded-lg">
        <div className="text-sm text-blue-600 mb-1">Total Records</div>
        <div className="text-2xl font-bold text-blue-900">{data.overall_summary?.total_invoices || 0}</div>
        <div className="text-xs text-blue-500 mt-1">{data.overall_summary?.invoice_count || 0} invoices + {data.overall_summary?.stock_out_count || 0} stock out</div>
      </div>
      <div className="bg-green-50 p-4 rounded-lg">
        <div className="text-sm text-green-600 mb-1">Total Sales</div>
        <div className="text-2xl font-bold text-green-900">₹{parseFloat(data.overall_summary?.total_sales || 0).toFixed(2)}</div>
      </div>
      <div className="bg-emerald-50 p-4 rounded-lg">
        <div className="text-sm text-emerald-600 mb-1">Total Paid</div>
        <div className="text-2xl font-bold text-emerald-900">₹{parseFloat(data.overall_summary?.total_paid || 0).toFixed(2)}</div>
      </div>
      <div className="bg-red-50 p-4 rounded-lg">
        <div className="text-sm text-red-600 mb-1">Total Due</div>
        <div className="text-2xl font-bold text-red-900">₹{parseFloat(data.overall_summary?.total_due || 0).toFixed(2)}</div>
      </div>
      <div className="bg-yellow-50 p-4 rounded-lg">
        <div className="text-sm text-yellow-600 mb-1">Tax Collected</div>
        <div className="text-2xl font-bold text-yellow-900">₹{parseFloat(data.overall_summary?.total_tax_collected || 0).toFixed(2)}</div>
      </div>
      <div className="bg-purple-50 p-4 rounded-lg">
        <div className="text-sm text-purple-600 mb-1">Avg Invoice Value</div>
        <div className="text-2xl font-bold text-purple-900">₹{parseFloat(data.overall_summary?.average_invoice_value || 0).toFixed(2)}</div>
      </div>
    </div>

    {/* Payment Status Summary */}
    {data.payment_status_summary && data.payment_status_summary.length > 0 && (
      <div className="mb-6">
        <h4 className="text-md font-semibold mb-3">Payment Status Breakdown</h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {data.payment_status_summary.map((status) => (
            <div key={status.payment_status} className={`p-4 rounded-lg ${
              status.payment_status === 'paid' ? 'bg-green-50' :
              status.payment_status === 'partial' ? 'bg-yellow-50' : 'bg-red-50'
            }`}>
              <div className={`text-sm mb-1 capitalize ${
                status.payment_status === 'paid' ? 'text-green-600' :
                status.payment_status === 'partial' ? 'text-yellow-600' : 'text-red-600'
              }`}>{status.payment_status}</div>
              <div className={`text-xl font-bold ${
                status.payment_status === 'paid' ? 'text-green-900' :
                status.payment_status === 'partial' ? 'text-yellow-900' : 'text-red-900'
              }`}>{status.count} invoices</div>
              <div className="text-xs text-gray-600">₹{parseFloat(status.total_amount || 0).toFixed(2)}</div>
            </div>
          ))}
        </div>
      </div>
    )}

    {/* Daily Summary Table */}
    {data.daily_summary && data.daily_summary.length > 0 && (
      <div>
        <h4 className="text-md font-semibold mb-3">Daily Summary</h4>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Invoices</th>
                <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Amount</th>
                <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Tax</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {data.daily_summary.map((day, index) => (
                <tr key={index} className="hover:bg-gray-50">
                  <td className="px-4 py-2 text-sm">{new Date(day.date).toLocaleDateString()}</td>
                  <td className="px-4 py-2 text-sm text-right">{day.invoice_count}</td>
                  <td className="px-4 py-2 text-sm text-right">₹{parseFloat(day.total_amount || 0).toFixed(2)}</td>
                  <td className="px-4 py-2 text-sm text-right">₹{parseFloat(day.total_tax || 0).toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    )}
  </div>
);

const LocationStockReport = ({ data }) => (
  <div>
    <h3 className="text-lg font-semibold mb-4">Location-wise Stock Report</h3>
    <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
      <div className="bg-blue-50 p-4 rounded-lg">
        <div className="text-sm text-blue-600 mb-1">Office Stock</div>
        <div className="text-xl font-bold text-blue-900">{data.location_totals.office.total_stock}</div>
        <div className="text-xs text-blue-600">₹{data.location_totals.office.total_value.toFixed(2)}</div>
      </div>
      <div className="bg-green-50 p-4 rounded-lg">
        <div className="text-sm text-green-600 mb-1">Godown Stock</div>
        <div className="text-xl font-bold text-green-900">{data.location_totals.godown.total_stock}</div>
        <div className="text-xs text-green-600">₹{data.location_totals.godown.total_value.toFixed(2)}</div>
      </div>
      <div className="bg-purple-50 p-4 rounded-lg">
        <div className="text-sm text-purple-600 mb-1">Factory Stock</div>
        <div className="text-xl font-bold text-purple-900">{data.location_totals.factory.total_stock}</div>
        <div className="text-xs text-purple-600">₹{data.location_totals.factory.total_value.toFixed(2)}</div>
      </div>
      <div className="bg-gray-50 p-4 rounded-lg">
        <div className="text-sm text-gray-600 mb-1">Total Stock</div>
        <div className="text-xl font-bold text-gray-900">{data.location_totals.overall.total_stock}</div>
        <div className="text-xs text-gray-600">₹{data.location_totals.overall.total_value.toFixed(2)}</div>
      </div>
    </div>
  </div>
);

const PartyLedgerReport = ({ data }) => (
  <div>
    <h3 className="text-lg font-semibold mb-4">Party Ledger Report</h3>
    <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
      <div className="bg-blue-50 p-4 rounded-lg">
        <div className="text-sm text-blue-600 mb-1">Total Customers</div>
        <div className="text-2xl font-bold text-blue-900">{data.summary?.total_customers || 0}</div>
      </div>
      <div className="bg-green-50 p-4 rounded-lg">
        <div className="text-sm text-green-600 mb-1">Total Business</div>
        <div className="text-2xl font-bold text-green-900">₹{parseFloat(data.summary?.total_business || 0).toFixed(2)}</div>
      </div>
      <div className="bg-purple-50 p-4 rounded-lg">
        <div className="text-sm text-purple-600 mb-1">Total Paid</div>
        <div className="text-2xl font-bold text-purple-900">₹{parseFloat(data.summary?.total_paid || 0).toFixed(2)}</div>
      </div>
      <div className="bg-red-50 p-4 rounded-lg">
        <div className="text-sm text-red-600 mb-1">Total Unpaid</div>
        <div className="text-2xl font-bold text-red-900">₹{parseFloat(data.summary?.total_unpaid || 0).toFixed(2)}</div>
      </div>
    </div>

    {/* Ledger Table */}
    {data.ledger && data.ledger.length > 0 && (
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Customer</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Source</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Company</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Phone</th>
              <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Transactions</th>
              <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Total Amount</th>
              <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Paid</th>
              <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Unpaid</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Last Date</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {data.ledger.map((customer, index) => (
              <tr key={`${customer.customer_id || customer.customer_name}-${index}`} className="hover:bg-gray-50">
                <td className="px-4 py-2 text-sm font-medium">{customer.customer_name}</td>
                <td className="px-4 py-2 text-sm">
                  <span className={`px-2 py-1 rounded-full text-xs ${
                    customer.source === 'stock_out' ? 'bg-orange-100 text-orange-800' : 'bg-blue-100 text-blue-800'
                  }`}>
                    {customer.source === 'stock_out' ? 'Stock Out' : 'Invoice'}
                  </span>
                </td>
                <td className="px-4 py-2 text-sm">{customer.company_name || '-'}</td>
                <td className="px-4 py-2 text-sm">{customer.customer_phone || '-'}</td>
                <td className="px-4 py-2 text-sm text-right">{customer.total_invoices}</td>
                <td className="px-4 py-2 text-sm text-right">₹{parseFloat(customer.total_amount || 0).toFixed(2)}</td>
                <td className="px-4 py-2 text-sm text-right text-green-600">₹{parseFloat(customer.total_paid || 0).toFixed(2)}</td>
                <td className="px-4 py-2 text-sm text-right text-red-600">₹{parseFloat(customer.total_unpaid || 0).toFixed(2)}</td>
                <td className="px-4 py-2 text-sm">{customer.last_invoice_date ? new Date(customer.last_invoice_date).toLocaleDateString() : '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}

    {(!data.ledger || data.ledger.length === 0) && (
      <p className="text-sm text-gray-600 text-center py-4">No customer data found</p>
    )}
  </div>
);

const PurchaseBillsReport = ({ data }) => (
  <div>
    <h3 className="text-lg font-semibold mb-4">Purchase Bills Report</h3>

    {/* Summary Cards */}
    <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
      <div className="bg-blue-50 p-4 rounded-lg">
        <div className="text-sm text-blue-600 mb-1">Total Bills</div>
        <div className="text-2xl font-bold text-blue-900">{data.summary?.total_bills || 0}</div>
      </div>
      <div className="bg-purple-50 p-4 rounded-lg">
        <div className="text-sm text-purple-600 mb-1">Total Amount</div>
        <div className="text-2xl font-bold text-purple-900">₹{parseFloat(data.summary?.total_amount || 0).toFixed(2)}</div>
      </div>
      <div className="bg-yellow-50 p-4 rounded-lg">
        <div className="text-sm text-yellow-600 mb-1">Total Tax</div>
        <div className="text-2xl font-bold text-yellow-900">₹{parseFloat(data.summary?.total_tax || 0).toFixed(2)}</div>
      </div>
      <div className="bg-green-50 p-4 rounded-lg">
        <div className="text-sm text-green-600 mb-1">Total Paid</div>
        <div className="text-2xl font-bold text-green-900">₹{parseFloat(data.summary?.total_paid || 0).toFixed(2)}</div>
      </div>
      <div className="bg-red-50 p-4 rounded-lg">
        <div className="text-sm text-red-600 mb-1">Total Due</div>
        <div className="text-2xl font-bold text-red-900">₹{parseFloat(data.summary?.total_due || 0).toFixed(2)}</div>
      </div>
    </div>

    {/* Payment Status Breakdown */}
    {data.summary?.by_status && (
      <div className="mb-6">
        <h4 className="text-md font-semibold mb-3">Payment Status Breakdown</h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-red-50 p-4 rounded-lg">
            <div className="text-sm text-red-600 mb-1">Unpaid</div>
            <div className="text-xl font-bold text-red-900">{data.summary.by_status.unpaid} bills</div>
            <div className="text-xs text-red-600">₹{parseFloat(data.summary.unpaid_amount || 0).toFixed(2)}</div>
          </div>
          <div className="bg-yellow-50 p-4 rounded-lg">
            <div className="text-sm text-yellow-600 mb-1">Partial</div>
            <div className="text-xl font-bold text-yellow-900">{data.summary.by_status.partial} bills</div>
            <div className="text-xs text-yellow-600">₹{parseFloat(data.summary.partial_amount || 0).toFixed(2)}</div>
          </div>
          <div className="bg-green-50 p-4 rounded-lg">
            <div className="text-sm text-green-600 mb-1">Paid</div>
            <div className="text-xl font-bold text-green-900">{data.summary.by_status.paid} bills</div>
            <div className="text-xs text-green-600">₹{parseFloat(data.summary.paid_amount || 0).toFixed(2)}</div>
          </div>
        </div>
      </div>
    )}

    {/* Monthly Summary */}
    {data.monthly_summary && data.monthly_summary.length > 0 && (
      <div className="mb-6">
        <h4 className="text-md font-semibold mb-3">Monthly Summary</h4>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Month</th>
                <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Bills</th>
                <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Total Amount</th>
                <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Tax</th>
                <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Paid</th>
                <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Due</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {data.monthly_summary.map((month, index) => (
                <tr key={index} className="hover:bg-gray-50">
                  <td className="px-4 py-2 text-sm font-medium">{month.month_name}</td>
                  <td className="px-4 py-2 text-sm text-right">{month.bill_count}</td>
                  <td className="px-4 py-2 text-sm text-right">₹{parseFloat(month.total_amount || 0).toFixed(2)}</td>
                  <td className="px-4 py-2 text-sm text-right">₹{parseFloat(month.total_tax || 0).toFixed(2)}</td>
                  <td className="px-4 py-2 text-sm text-right text-green-600">₹{parseFloat(month.total_paid || 0).toFixed(2)}</td>
                  <td className="px-4 py-2 text-sm text-right text-red-600">₹{parseFloat(month.total_due || 0).toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    )}

    {/* Top Suppliers */}
    {data.top_suppliers && data.top_suppliers.length > 0 && (
      <div className="mb-6">
        <h4 className="text-md font-semibold mb-3">Top Suppliers</h4>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Supplier</th>
                <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Bills</th>
                <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Total Amount</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {data.top_suppliers.map((supplier, index) => (
                <tr key={index} className="hover:bg-gray-50">
                  <td className="px-4 py-2 text-sm font-medium">{supplier.supplier_name}</td>
                  <td className="px-4 py-2 text-sm text-right">{supplier.bill_count}</td>
                  <td className="px-4 py-2 text-sm text-right">₹{parseFloat(supplier.total_amount || 0).toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    )}

    {/* Bills Table */}
    {data.bills && data.bills.length > 0 && (
      <div>
        <h4 className="text-md font-semibold mb-3">Bill Details</h4>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Bill #</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Supplier</th>
                <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Amount</th>
                <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Paid</th>
                <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Due</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {data.bills.map((bill) => (
                <tr key={bill.id} className="hover:bg-gray-50">
                  <td className="px-4 py-2 text-sm font-medium">{bill.bill_number}</td>
                  <td className="px-4 py-2 text-sm">{new Date(bill.bill_date).toLocaleDateString()}</td>
                  <td className="px-4 py-2 text-sm">{bill.supplier_name}</td>
                  <td className="px-4 py-2 text-sm text-right">₹{parseFloat(bill.total_amount || 0).toFixed(2)}</td>
                  <td className="px-4 py-2 text-sm text-right text-green-600">₹{parseFloat(bill.paid_amount || 0).toFixed(2)}</td>
                  <td className="px-4 py-2 text-sm text-right text-red-600">₹{parseFloat(bill.due_amount || 0).toFixed(2)}</td>
                  <td className="px-4 py-2 text-sm">
                    <span className={`px-2 py-1 rounded-full text-xs ${
                      bill.payment_status === 'paid' ? 'bg-green-100 text-green-800' :
                      bill.payment_status === 'partial' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-red-100 text-red-800'
                    }`}>
                      {bill.payment_status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-sm text-gray-600 mt-4">Showing {data.bills.length} purchase bills</p>
      </div>
    )}

    {(!data.bills || data.bills.length === 0) && (
      <p className="text-sm text-gray-600 text-center py-4">No purchase bills found for the selected period</p>
    )}
  </div>
);

export default Reports;
